import { defineStore } from "pinia";
import type {
  CastScreen,
  CastScreenConfig,
} from "@/stores/cast-screen/types.d.ts";
import {
  EditSuggestion,
  Suggestion,
} from "@/stores/change-suggestion/types.ts";
import { useStore } from "vuex";
import { useHttpStore } from "@/stores/http/index.ts";
import { ExternalType, MediaItem, RawMediaCollection } from "@/types/media.ts";
import { computed } from "vue";
import {
  FolderLocation,
  SearchOption,
  SiteAnalytics,
  DatadogSiteAnalytics,
  Tweet,
  Calendar,
  BulkRecordDoc,
} from "@/types/misc.ts";
import { Site, SiteDocument } from "@/types/site.ts";
import { User } from "@/types/user.ts";
import { CategoriesDocument } from "@/types/categories.ts";
import { CategoryDocument, PartialCategoryDocument } from "@/types/category.ts";
import { EntryDocument } from "@/types/entry.ts";
import { RecentlyDeleted } from "@/cms/components/RecentlyDeletedTable/RecentlyDeletedTable.types.ts";
import { SearchOptionV2 } from "@/util/useSearchV2.ts";
import {
  PatchMediaMetadata,
  PatchUser,
  PatchCategories,
  PatchCategory,
  PatchEntry,
  ApiResponse,
  GenericResponse,
  GenericDocumentResponse,
  CopyEntryResponse,
  AddEntriesBulkResponse,
  Authorization,
  DeleteBulk,
  PatchBulk,
  PatchBulkResponse,
  MostCommonLayoutResponse,
  RestoreDocumentResponse,
  LastModifiedResponse,
  CreateUser,
  CreateUserResponse,
  InitiateAccountLinkingResponse,
  ContinueAccountLinkingResponse,
} from "@/stores/api/types.ts";
import type { SiteIDDocumentUpdate } from "@rocketalumnisolutions/backend-types/documents/site-id";
import { deobfuscate, obfuscate } from "@/util/crypto.ts";
import { CategoryTreeDocument } from "@/types/category-tree.ts";

export const useApiStore = defineStore("api", () => {
  const httpStore = useHttpStore();
  const store = useStore();

  const clientId = computed(() => store.getters.getClientID);
  const siteId = computed(() => store.getters.getStyleID);
  const styleName = computed(() => store.getters.getOrganizationName);
  const title = computed<string>(
    () =>
      store.getters.getStoreVar("Home", "styling", "text", "title") ??
      "Hall of Fame"
  );

  const http = computed(() => httpStore.axios);

  async function fetchCategory(
    categoryId: string,
    id?: string
  ): Promise<ApiResponse<CategoryDocument>> {
    return http.value.get(
      `getDocument/${id || clientId.value}/category/${categoryId}/`
    );
  }

  async function fetchCategories(): Promise<ApiResponse<CategoriesDocument>> {
    return http.value.get(`getDocument/${clientId.value}/categories/`);
  }
  async function fetchAllCategories(
    id?: string
  ): Promise<ApiResponse<PartialCategoryDocument[]>> {
    return http.value.get(`allCategories/${id || clientId.value}/`);
  }
  async function fetchMoveDocumentDestinations(
    documentType: "entry" | "category"
  ): Promise<ApiResponse<FolderLocation[]>> {
    return http.value.get(
      `moveDocumentDestinations/${clientId.value}/${documentType}?pathLabels=true`
    );
  }

  async function fetchSearchOptions(): Promise<ApiResponse<SearchOption[]>> {
    return http.value.get(`searchOptions/${clientId.value}/`);
  }

  async function fetchSearchOptionsV2(
    query: string,
    hydrateMedia = true
  ): Promise<ApiResponse<SearchOptionV2[]>> {
    return http.value.post(`v2/searchOptions/${clientId.value}/`, {
      query,
      hydrateMedia,
    });
  }

  async function fetchCategoryTree(
    allCategories: boolean
  ): Promise<ApiResponse<CategoryTreeDocument[]>> {
    const endpoint = `categoryTree/${clientId.value}`;
    const url =
      allCategories !== undefined
        ? `${endpoint}?allCategories=${allCategories}`
        : endpoint;
    return http.value.get(url);
  }

  async function fetchRecentlyDeleted(): Promise<
    ApiResponse<RecentlyDeleted[]>
  > {
    return http.value.get(`recentlyDeleted/${clientId.value}/`);
  }
  async function patchCategory(
    id: string,
    patch: PatchCategory
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    const path = `patchDocument/${clientId.value}/category/${id}`;
    return http.value.patch(path, patch);
  }
  async function patchCategories(
    patch: PatchCategories
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    const path = `patchDocument/${clientId.value}/categories`;
    return http.value.patch(path, patch);
  }
  async function patchEntry(
    id: string,
    patch: PatchEntry
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    const path = `patchDocument/${clientId.value}/entry/${id}`;
    return http.value.patch(path, patch);
  }

  async function deleteCategory(
    id: string
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value.delete(`deleteCategory/${clientId.value}/${id}/`);
  }
  async function deleteEntry(
    id: string
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.delete(`deleteEntry/${clientId.value}/${id}/`);
  }
  async function addCategory(
    doc: CategoryDocument
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(`addCategory/${clientId.value}`, doc);
  }
  async function addSubCategory(
    categoryId: string,
    doc: CategoryDocument
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(`addCategory/${clientId.value}/${categoryId}`, doc);
  }
  async function addEntry(
    categoryId: string,
    doc: EntryDocument
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(`addEntry/${clientId.value}/${categoryId}`, doc);
  }
  async function updateEntries(
    categoryId: string,
    doc: EntryDocument
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(
      `updateAllEntriesInCategory/${clientId.value}/${categoryId}/`,
      doc
    );
  }
  async function switchCategoryType(
    categoryId: string
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(
      `switchCategoryType/${clientId.value}/${categoryId}/`
    );
  }
  async function moveEntry(
    destinationCategoryId: string,
    documentIds: string[]
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value.post(
      `entry/move/${clientId.value}/${destinationCategoryId}/`,
      documentIds
    );
  }
  async function moveCategory(
    destinationCategoryId: string,
    documentIds: string[]
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value.post(
      `category/move/${clientId.value}/${destinationCategoryId}/`,
      documentIds
    );
  }
  async function copyCategoryStructure(
    targetCategoryId: string
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(
      `copyCategoryStructure/${clientId.value}/${targetCategoryId}/`
    );
  }
  async function bulkRecordUpload(
    categoryId: string,
    doc: BulkRecordDoc
  ): Promise<ApiResponse<GenericDocumentResponse>> {
    return http.value.post(
      `RecordBulkUpload/${clientId.value}/${categoryId}/`,
      doc
    );
  }
  async function fetchCalendar(url: string): Promise<ApiResponse<Calendar[]>> {
    if (import.meta.env.VITE_API_URL.includes("localhost")) {
      console.warn("Calendar data is not loaded by local API.");
    }

    // If the `Authorization` header is globally set, it causes
    // the `/calendarData` endpoint to fail with a CORS error.
    // Trying to overwrite by specifying `headers` in the request
    // options doesn't work, so we need to remove the header
    // globally, then re-add it after the request.
    const defaultAuthHeader = http.value.defaults.headers.Authorization;
    delete http.value.defaults.headers.Authorization;

    const response = await http.value.get("/calendarData", {
      params: {
        url,
      },
    });

    http.value.defaults.headers.Authorization = defaultAuthHeader;

    return response;
  }
  async function restoreDocument(
    documentType: "category" | "entry",
    documentId: string
  ): Promise<ApiResponse<RestoreDocumentResponse>> {
    return http.value.post(
      `restore/${clientId.value}/${documentType}/${documentId}/`
    );
  }

  async function patchSite(
    patch: SiteIDDocumentUpdate
  ): Promise<ApiResponse<{ id: string; message: string }>> {
    const path = `siteID/${siteId.value}`;
    return http.value.patch(path, patch);
  }
  async function fetchMostCommonEntryLayout(
    categoryId: string
  ): Promise<ApiResponse<MostCommonLayoutResponse>> {
    const path = `mostCommonEntryLayout/${clientId.value}/${categoryId}`;
    return http.value.get(path);
  }
  async function fetchLastUpdate(
    isTouchScreen: boolean,
    isFullscreen: boolean
  ): Promise<ApiResponse<LastModifiedResponse>> {
    const path = `lastModified/${siteId.value}?isTouchScreen=${isTouchScreen}&isFullscreen=${isFullscreen}`;
    return http.value.get(path);
  }
  async function fetchStylingData(
    id?: string
  ): Promise<ApiResponse<SiteDocument>> {
    return http.value.get(`siteID/${id || siteId.value}`);
  }
  async function fetchUsers(): Promise<ApiResponse<User[]>> {
    const path = `users/${siteId.value}`;
    return http.value.get(path);
  }
  async function patchUser(
    patch: PatchUser
  ): Promise<ApiResponse<GenericResponse>> {
    const path = `users/${siteId.value}`;
    return http.value.patch(path, patch);
  }
  async function deleteUser(
    userID: string
  ): Promise<ApiResponse<GenericResponse>> {
    const path = `users/${siteId.value}`;
    return http.value({ method: "delete", url: path, data: { userID } });
  }
  async function addUser(
    user: CreateUser
  ): Promise<ApiResponse<CreateUserResponse>> {
    const path = `users`;
    return http.value.post(path, user);
  }
  async function patchUserAccess(
    email: string
  ): Promise<ApiResponse<GenericResponse>> {
    const path = `grantSiteAccess`;
    return http.value.post(path, { email, siteID: siteId.value });
  }

  async function authorizeUser(): Promise<ApiResponse<Authorization>> {
    const path = `authorize/${siteId.value}`;
    return http.value.post(path);
  }

  async function signAgreements(): Promise<ApiResponse<GenericResponse>> {
    const path = `signAgreements/${siteId.value}`;
    return http.value.patch(path);
  }

  async function lookupSiteId(): Promise<ApiResponse<{ siteID: string }>> {
    return http.value.get(`userSite/`);
  }
  async function userSiteIds(): Promise<ApiResponse<{ siteIDs: string[] }>> {
    return http.value.get(`userSiteIDs/`);
  }
  async function patchBulk(
    updates: PatchBulk
  ): Promise<ApiResponse<PatchBulkResponse>> {
    return http.value.patch(`bulk/${clientId.value}`, { updates });
  }
  async function deleteBulk(
    deletions: DeleteBulk
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value({
      method: "delete",
      url: `bulk/${clientId.value}`,
      data: { deletions },
    });
  }
  async function patchMediaMetaData(
    metadata: PatchMediaMetadata,
    mediaId: string
  ): Promise<ApiResponse<{ metadata: MediaMetadata }>> {
    return http.value.patch(`media/metadata/${mediaId}`, { metadata });
  }

  async function addExternalMedia(
    docType: "siteID" | "collection",
    id: string,
    url: string,
    externalType: ExternalType,
    name: string
  ): Promise<ApiResponse<MediaItem>> {
    return http.value.post(`externalMedia/${docType}/${id}`, {
      url,
      externalType,
      name,
    });
  }

  async function fetchAnalytics(
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<{ analytics: SiteAnalytics }>> {
    const path = `analytics/${siteId.value}?startDate=${startDate}&endDate=${endDate}`;
    return http.value.get(path);
  }

  async function fetchDatadogAnalytics(
    range: string
  ): Promise<ApiResponse<{ analytics: DatadogSiteAnalytics }>> {
    const path = `datadogAnalytics/${siteId.value}?range=${range}`;
    return http.value.get(path);
  }

  async function fetchTweets(
    username: string,
    buttonId: string
  ): Promise<ApiResponse<{ tweets: Tweet[] }>> {
    const path = `tweets/${siteId.value}/${username}/${buttonId}`;
    return http.value.get(path);
  }

  async function confirmTwitter(
    username: string,
    buttonId: string
  ): Promise<ApiResponse<{ confirmed: boolean }>> {
    return http.value.patch(`confirmTwitter/${siteId.value}`, {
      username,
      buttonId,
    });
  }
  async function addEntriesBulk(
    categoryId: string,
    entries: PatchEntry[]
  ): Promise<ApiResponse<AddEntriesBulkResponse>> {
    return http.value.post(`bulk/entry/${clientId.value}/${categoryId}/`, {
      entries,
    });
  }

  async function copyEntry(
    destinationCategoryId: string,
    entryId: string
  ): Promise<ApiResponse<CopyEntryResponse>> {
    return http.value.post(
      `copyEntry/${clientId.value}/${destinationCategoryId}`,
      {
        entryId,
      }
    );
  }

  async function fetchAllScreens(): Promise<ApiResponse<CastScreen[]>> {
    return http.value.get(`cast-screens/get-all/${siteId.value}`);
  }
  async function fetchScreen(
    screenID: string
  ): Promise<ApiResponse<CastScreen>> {
    return http.value.get(`cast-screens/get-one/${screenID}`);
  }
  async function addScreen(
    screenName: string
  ): Promise<ApiResponse<{ screen: CastScreen; message: string }>> {
    return http.value.post(`cast-screens/${siteId.value}`, {
      name: screenName,
      clientID: clientId.value,
    });
  }
  async function deleteScreen(
    screenID: string
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value.delete(`cast-screens/${siteId.value}/${screenID}`);
  }
  async function updateScreen(
    screenID: string,
    config: CastScreenConfig,
    screenName?: string,
    categoryIds?: string[]
  ): Promise<ApiResponse<{ message: string; screen: CastScreen }>> {
    return http.value.patch(`cast-screens/${siteId.value}/${screenID}`, {
      name: screenName,
      categoryIds,
      config,
    });
  }

  async function createEditSuggestion(
    params: EditSuggestion
  ): Promise<ApiResponse<{ contentSuggestion: Suggestion; message: string }>> {
    const { collectionID, entryID, changes } = params;

    return http.value.post("content-suggestions", {
      collectionID,
      entryID,
      changes,
      type: "edit",
    });
  }

  async function getEditSuggestions(): Promise<ApiResponse<Suggestion[]>> {
    return http.value.get(`content-suggestions/${clientId.value}`);
  }

  async function approveEditSuggestion(
    suggestionID: string
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value.patch(
      `content-suggestions/${clientId.value}/${suggestionID}`
    );
  }

  async function rejectEditSuggestion(
    suggestionID: string,
    rejectReason: string
  ): Promise<ApiResponse<GenericResponse>> {
    return http.value({
      method: "delete",
      url: `content-suggestions/${clientId.value}/${suggestionID}`,
      data: {
        rejectReason,
      },
    });
  }

  async function getSites(searchQuery: string): Promise<ApiResponse<Site[]>> {
    return http.value.get(`sites`, {
      params: {
        limit: 20,
        page: 1,
        styleName: searchQuery,
      },
    });
  }

  async function initiateAccountLinking(
    sessionToken: string
  ): Promise<ApiResponse<InitiateAccountLinkingResponse>> {
    return http.value.get("account-linking/initiate", {
      params: { sessionToken },
    });
  }

  async function continueAccountLinking(
    sessionToken: string,
    state: string
  ): Promise<ApiResponse<ContinueAccountLinkingResponse>> {
    return http.value.get("account-linking/continue", {
      params: { sessionToken, state },
    });
  }

  async function searchMedia({
    page,
    limit,
    textQuery,
    vectorQuery,
    tags,
    signal,
    hideInDAM,
  }: {
    page: number;
    limit: number;
    textQuery: string;
    vectorQuery: string;
    tags: string[];
    signal?: AbortSignal;
    hideInDAM?: boolean;
  }): Promise<
    ApiResponse<{
      media: RawMediaCollection;
      pagination: {
        totalItems: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      };
    }>
  > {
    return http.value.get(`collections/${clientId.value}/media/search`, {
      params: {
        page,
        limit,
        textQuery,
        vectorQuery,
        tags: tags.map((tag) => tag.toLowerCase()).join(","),
        hideInDAM,
      },
      signal,
    });
  }

  type TouchscreenMediaResponse = {
    images: string[];
    videos: string[];
    iframes: {
      url: string;
      sheetName?: string;
    }[];
    other: string[];
  };

  async function fetchTouchscreenMedia(): Promise<
    ApiResponse<TouchscreenMediaResponse>
  > {
    const storageKey = `ftm-${siteId.value}`;

    const emptyCache: TouchscreenMediaResponse = {
      images: [],
      videos: [],
      iframes: [],
      other: [],
    };

    const rawCache = window.localStorage.getItem(storageKey);

    let fallbackCache: TouchscreenMediaResponse = emptyCache;
    let rawResponse: ApiResponse<string>;

    try {
      if (rawCache) {
        fallbackCache = JSON.parse(rawCache);
      }
    } catch (e1) {
      console.error(e1);
      try {
        window.localStorage.removeItem(storageKey);
      } catch (e2) {
        console.error(e2);
      }
    }

    try {
      rawResponse = (await http.value.get(`s/${siteId.value}/m`, {
        params: {
          sbt: "1",
        },
        headers: {
          "x-c": await obfuscate(clientId.value, styleName.value, title.value),
        },
      })) as ApiResponse<string>;
    } catch (e3) {
      console.error(e3);

      return {
        data: fallbackCache,
      };
    }

    const payload = (await deobfuscate(
      rawResponse.data,
      styleName.value,
      title.value
    )) as string;

    window.localStorage.setItem(storageKey, payload);

    return {
      data: JSON.parse(
        payload ?? JSON.stringify(emptyCache)
      ) as TouchscreenMediaResponse,
    };
  }

  return {
    searchMedia,
    addCategory,
    addSubCategory,
    addEntry,
    fetchCategory,
    fetchCategories,
    fetchAllCategories,
    fetchMoveDocumentDestinations,
    fetchSearchOptions,
    fetchSearchOptionsV2,
    fetchCategoryTree,
    fetchRecentlyDeleted,
    patchCategory,
    deleteCategory,
    deleteEntry,
    updateEntries,
    bulkRecordUpload,
    switchCategoryType,
    moveEntry,
    moveCategory,
    fetchCalendar,
    restoreDocument,
    patchEntry,
    patchSite,
    fetchMostCommonEntryLayout,
    patchCategories,
    fetchLastUpdate,
    fetchStylingData,
    fetchUsers,
    patchUser,
    deleteUser,
    addUser,
    patchUserAccess,
    copyCategoryStructure,
    authorizeUser,
    signAgreements,
    lookupSiteId,
    userSiteIds,
    patchBulk,
    deleteBulk,
    patchMediaMetaData,
    addExternalMedia,
    fetchAnalytics,
    fetchDatadogAnalytics,
    fetchTweets,
    confirmTwitter,
    addEntriesBulk,
    copyEntry,
    fetchAllScreens,
    fetchScreen,
    addScreen,
    deleteScreen,
    updateScreen,
    createEditSuggestion,
    getEditSuggestions,
    approveEditSuggestion,
    rejectEditSuggestion,
    getSites,
    fetchTouchscreenMedia,
    initiateAccountLinking,
    continueAccountLinking,
  };
});
